import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { CLIENT_USER_ROLE } from '@api/types/client-user-management';
import MultiSelectAccordion from './MultiSelectAccordion';
import RoleCard from './RoleCard';
import { ROLE_DISPLAY_LABELS } from '../constants';
import type { MultiSelectAccordionMode } from './MultiSelectAccordion';
import { FilterOption } from '@api/types/common';

const labelStyles = {
  fontSize: '1rem',
  fontWeight: 700,
  lineHeight: '1.375rem',
  color: '#162936',
};

type UserAccessFormProps = {
  selectedRole: CLIENT_USER_ROLE;
  selectedDashboardIds: string[];
  dashboardOptions: FilterOption[];
  dashboardAccordionExpanded: boolean;
  onSelectRole: (role: CLIENT_USER_ROLE) => void;
  onToggleDashboard: (dashboardId: string) => void;
  onSelectAllDashboards: () => void;
  onClearAllDashboards: () => void;
  onAccordionExpand: (expanded: boolean) => void;
  isLoadingDashboards?: boolean;
};

const ROLE_CARD_DESCRIPTION: Record<CLIENT_USER_ROLE, string> = {
  [CLIENT_USER_ROLE.OWNER]: 'Can access all dashboards and manage users',
  [CLIENT_USER_ROLE.FULL_MEMBER]: 'Can access all dashboards',
  [CLIENT_USER_ROLE.MEMBER]: 'Can access only selected dashboards',
};

const ALL_DASHBOARDS_READONLY_METADATA = {
  mode: 'readonly' as MultiSelectAccordionMode,
  title: 'Included Dashboards',
  description: 'Invited Users will have access to all dashboards',
};

const getDashboardSelectorAccordionMetadata = (
  role: CLIENT_USER_ROLE,
  selectedDashboardsCount: number,
  totalDashboardsCount: number,
) => {
  switch (role) {
    case CLIENT_USER_ROLE.OWNER:
      return {
        mode: 'readonly' as MultiSelectAccordionMode,
        title: 'Included Dashboards',
        description: 'Owners will have access to all dashboards and can manage users',
      };
    case CLIENT_USER_ROLE.FULL_MEMBER:
      return ALL_DASHBOARDS_READONLY_METADATA;
    case CLIENT_USER_ROLE.MEMBER: {
      const countSuffix =
        selectedDashboardsCount && totalDashboardsCount
          ? ` (${selectedDashboardsCount}/${totalDashboardsCount})`
          : '';
      return {
        mode: 'select' as MultiSelectAccordionMode,
        title: `Select Dashboards${countSuffix}`,
        description: 'Members will have access only to selected dashboards',
      };
    }
    default:
      return ALL_DASHBOARDS_READONLY_METADATA;
  }
};

const UserAccessForm: FC<UserAccessFormProps> = ({
  selectedRole,
  dashboardOptions,
  selectedDashboardIds,
  dashboardAccordionExpanded,
  onSelectRole,
  onToggleDashboard,
  onSelectAllDashboards,
  onClearAllDashboards,
  onAccordionExpand,
  isLoadingDashboards = false,
}) => {
  const accordion = getDashboardSelectorAccordionMetadata(
    selectedRole,
    selectedDashboardIds.length,
    dashboardOptions.length,
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <Typography sx={labelStyles}>Role</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '0.75rem',
            width: '100%',
          }}
        >
          {Object.values(CLIENT_USER_ROLE).map((role) => (
            <RoleCard
              key={role}
              role={role}
              label={ROLE_DISPLAY_LABELS[role]}
              description={ROLE_CARD_DESCRIPTION[role]}
              isSelected={selectedRole === role}
              onSelect={() => onSelectRole(role)}
            />
          ))}
        </Box>
      </Box>
      <MultiSelectAccordion
        {...accordion}
        isExpanded={dashboardAccordionExpanded}
        selectedItems={selectedDashboardIds}
        listItems={dashboardOptions}
        onExpand={onAccordionExpand}
        onSelectItem={onToggleDashboard}
        onSelectAll={onSelectAllDashboards}
        onClearAll={onClearAllDashboards}
        isLoading={isLoadingDashboards}
      />
    </>
  );
};

export default UserAccessForm;
